import {
  mapResourceSetToEditPayload,
  remapMangledThreatChecklistKeys,
  type ResourceSet,
  type ResourceThreatChecklists,
} from 'components/IAP/hooks/resourceSetHooks';
import type { OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';

export type AutomationRuleTypeRef = {
  name: string;
  archived?: boolean;
  event_template?: { type?: string | null };
};

export type ResourceChecklistFields = {
  threats?: string[];
  threat_checklists?: ResourceThreatChecklists;
  automation_rules?: string[];
  automation_rule_checklists?: ResourceThreatChecklists;
};

export function ruleTypeToNameMap(
  rules: AutomationRuleTypeRef[] | undefined
): Map<string, string> {
  const map = new Map<string, string>();
  for (const rule of rules ?? []) {
    if (rule.archived) continue;
    const type = rule.event_template?.type?.trim();
    const name = rule.name.trim();
    if (!type || !name) continue;
    if (!map.has(type)) map.set(type, name);
  }
  return map;
}

/**
 * Prefer persisted rule-keyed checklists. If those columns are empty, derive
 * them from threat-keyed data using each group rule's event_template.type.
 */
export function resolveResourceAutomationRuleChecklists(
  resource: ResourceChecklistFields,
  rules?: AutomationRuleTypeRef[]
): {
  automationRules: string[];
  automationRuleChecklists: ResourceThreatChecklists;
} {
  const storedRules = (resource.automation_rules ?? []).filter(
    (name) => name.trim().length > 0
  );
  const storedChecklists = remapMangledThreatChecklistKeys(
    resource.automation_rule_checklists,
    storedRules
  );
  const storedHasData =
    storedRules.length > 0 ||
    Object.values(storedChecklists).some(
      (items) => Array.isArray(items) && items.length > 0
    );
  if (storedHasData) {
    const keys = storedRules.length
      ? storedRules
      : Object.keys(storedChecklists).filter(
          (key) => (storedChecklists[key] ?? []).length > 0
        );
    return {
      automationRules: keys,
      automationRuleChecklists: storedChecklists,
    };
  }

  const threats = (resource.threats ?? []).filter(
    (threat) => threat.trim().length > 0
  );
  const threatChecklists = remapMangledThreatChecklistKeys(
    resource.threat_checklists,
    threats
  );
  const typeToName = ruleTypeToNameMap(rules);
  const automationRules: string[] = [];
  const automationRuleChecklists: ResourceThreatChecklists = {};

  for (const threat of threats) {
    const ruleName = typeToName.get(threat);
    if (!ruleName) continue;
    if (!automationRules.includes(ruleName)) automationRules.push(ruleName);
    const items = threatChecklists[threat];
    if (Array.isArray(items) && items.length > 0) {
      automationRuleChecklists[ruleName] = items;
    }
  }

  for (const [threat, items] of Object.entries(threatChecklists)) {
    if (!Array.isArray(items) || items.length === 0) continue;
    const ruleName = typeToName.get(threat);
    if (!ruleName) continue;
    if (!automationRules.includes(ruleName)) automationRules.push(ruleName);
    if (!automationRuleChecklists[ruleName]?.length) {
      automationRuleChecklists[ruleName] = items;
    }
  }

  return { automationRules, automationRuleChecklists };
}

/** Dual-write rule-keyed fields plus threat-keyed copies for rollback. */
export function dualWriteFromRuleSelection(
  selectedRuleNames: string[],
  ruleChecklists: ResourceThreatChecklists | undefined,
  rules: AutomationRuleTypeRef[] | undefined
): {
  automationRules: string[];
  automationRuleChecklists: ResourceThreatChecklists;
  threats: string[];
  threatChecklists: ResourceThreatChecklists;
} {
  const names = selectedRuleNames.filter((name) => name.trim().length > 0);
  const remapped = remapMangledThreatChecklistKeys(ruleChecklists, names);
  const nameToType = new Map<string, string>();
  for (const rule of rules ?? []) {
    if (rule.archived) continue;
    const type = rule.event_template?.type?.trim();
    if (type) nameToType.set(rule.name.trim(), type);
  }

  const automationRuleChecklists: ResourceThreatChecklists = {};
  for (const name of names) {
    automationRuleChecklists[name] = remapped[name] ?? [];
  }

  const threats: string[] = [];
  const threatChecklists: ResourceThreatChecklists = {};
  for (const name of names) {
    const type = nameToType.get(name);
    if (!type) continue;
    if (!threats.includes(type)) threats.push(type);
    const items = automationRuleChecklists[name] ?? [];
    if (items.length > 0 && !(threatChecklists[type]?.length > 0)) {
      threatChecklists[type] = items;
    }
  }

  return {
    automationRules: names,
    automationRuleChecklists,
    threats,
    threatChecklists,
  };
}

export function resourceHasAutomationRule(
  resource: ResourceChecklistFields,
  ruleName: string,
  rules?: AutomationRuleTypeRef[]
): boolean {
  const trimmed = ruleName.trim();
  if (!trimmed) return false;
  const resolved = resolveResourceAutomationRuleChecklists(resource, rules);
  if (resolved.automationRules.includes(trimmed)) return true;
  return (resolved.automationRuleChecklists[trimmed] ?? []).length > 0;
}

/**
 * Assign, replace, or remove one rule’s checklist on a resource without
 * clobbering other rules. Dual-writes legacy category keys.
 */
export function mergeResourceChecklistForRule(params: {
  resource: ResourceChecklistFields;
  ruleName: string;
  items?: OrgEventChecklistItemDef[];
  remove?: boolean;
  rules?: AutomationRuleTypeRef[];
}): ReturnType<typeof dualWriteFromRuleSelection> {
  const trimmed = params.ruleName.trim();
  const resolved = resolveResourceAutomationRuleChecklists(
    params.resource,
    params.rules
  );
  let names = resolved.automationRules.filter((name) => name.trim().length > 0);
  const checklists: ResourceThreatChecklists = {
    ...resolved.automationRuleChecklists,
  };

  if (params.remove || !trimmed) {
    names = names.filter((name) => name !== trimmed);
    delete checklists[trimmed];
  } else {
    if (!names.includes(trimmed)) names = [...names, trimmed];
    checklists[trimmed] = params.items ?? [];
  }

  return dualWriteFromRuleSelection(names, checklists, params.rules);
}

/** Full resource PUT body: existing fields plus the merged checklist columns. */
export function resourceChecklistUpdatePayload(
  resource: ResourceSet,
  fields: ReturnType<typeof dualWriteFromRuleSelection>
) {
  return {
    ...mapResourceSetToEditPayload(resource.resource_set_id, resource),
    ...fields,
  };
}
